// SCR-STAFF-954 — لاگ و گزارش پیام (US-MSG-070؛ AP-MSG-01): همه پیام‌های مستأجر با زمان، هدف، قالب، کانال (پیامک، ایمیل، Push)، وضعیت
// (Queued، Deferred، Suppressed، Sent، Delivered، Failed، Undeliverable، Expired، Cancelled) و علت؛ فیلتر وضعیت و هدف؛ صفحه‌بندی Cursor.
// متن پیام (۹۰ روز) فقط با msg.log.view_content در جزئیات (COMP-PATIENT-MESSAGES مشترک). زبانه «مصرف و تحویل» (US-MSG-072؛ AP-MSG-02) فقط
// با msg.report.view.
import Button from '@mui/material/Button';
import MenuItem from '@mui/material/MenuItem';
import Stack from '@mui/material/Stack';
import Tab from '@mui/material/Tab';
import Tabs from '@mui/material/Tabs';
import TextField from '@mui/material/TextField';
import { unwrap, type MessageLogItem } from '@phoenix/api-client';
import { useAccess, useTenantSession } from '@phoenix/auth';
import { useTranslation } from '@phoenix/i18n';
import { DataTable, ListPage } from '@phoenix/ui';
import { useInfiniteQuery } from '@tanstack/react-query';
import { useState } from 'react';
import { msgKeys, SCR_STAFF_954 } from './common';
import { MessageDetailDialog, useMessageColumns } from './MessageViews';
import { UsageReportPanel } from './UsageReport';

const STATUSES = [
  '',
  'Queued',
  'Deferred',
  'Suppressed',
  'Sent',
  'Delivered',
  'Failed',
  'Undeliverable',
  'Expired',
  'Cancelled',
] as const;
const PURPOSES = ['', 'Otp', 'Transactional', 'Marketing', 'Critical'] as const;

export function MessageLogPage() {
  const { t } = useTranslation('msg');
  const { tenantApi, session } = useTenantSession();
  const [status, setStatus] = useState<string>('');
  const [purpose, setPurpose] = useState<string>('');
  const [open, setOpen] = useState<MessageLogItem | null>(null);
  const canReport = useAccess().can('msg.report.view');
  const [tab, setTab] = useState<'log' | 'usage'>('log');
  const log = useInfiniteQuery({
    queryKey: msgKeys.log(session?.tenantId, status, purpose),
    initialPageParam: undefined as string | undefined,
    queryFn: ({ pageParam }) =>
      unwrap(
        tenantApi.GET('/api/v1/messaging/messages', {
          params: {
            query: {
              cursor: pageParam,
              limit: 50,
              status: status || undefined,
              purpose: purpose || undefined,
            },
          },
        }),
      ),
    getNextPageParam: (page) => page.nextCursor ?? undefined,
  });
  const columns = useMessageColumns(setOpen, true);
  const rows = log.data?.pages.flatMap((page) => page.items);

  return (
    <ListPage
      screenId={SCR_STAFF_954}
      titleKey="msg:log.title"
      subtitleKey="msg:log.subtitle"
      filters={
        <Stack spacing={1}>
          {canReport ? (
            <Tabs
              value={tab}
              onChange={(_, value: 'log' | 'usage') => setTab(value)}
              aria-label={t('msg:log.title')}
            >
              <Tab value="log" label={t('msg:log.tabs.log')} data-testid="log-tab-log" />
              <Tab value="usage" label={t('msg:log.tabs.usage')} data-testid="log-tab-usage" />
            </Tabs>
          ) : null}
          {tab === 'log' ? (
            <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1}>
              <TextField
                select
                size="small"
                label={t('msg:log.filter_status')}
                value={status}
                onChange={(event) => setStatus(event.target.value)}
                sx={{ minWidth: 200 }}
                slotProps={{ htmlInput: { 'data-testid': 'log-filter-status' } }}
              >
                {STATUSES.map((value) => (
                  <MenuItem key={value || 'all'} value={value}>
                    {value ? t(`msg:status.${value}`) : t('msg:log.all')}
                  </MenuItem>
                ))}
              </TextField>
              <TextField
                select
                size="small"
                label={t('msg:log.filter_purpose')}
                value={purpose}
                onChange={(event) => setPurpose(event.target.value)}
                sx={{ minWidth: 200 }}
              >
                {PURPOSES.map((value) => (
                  <MenuItem key={value || 'all'} value={value}>
                    {value ? t(`msg:purpose.${value}`) : t('msg:log.all')}
                  </MenuItem>
                ))}
              </TextField>
            </Stack>
          ) : null}
        </Stack>
      }
    >
      {tab === 'usage' && canReport ? <UsageReportPanel /> : null}
      {tab === 'log' ? (
        <>
          <DataTable
            captionKey="msg:log.caption"
            columns={columns}
            rows={rows}
            getRowId={(row) => row.id}
            isLoading={log.isPending}
            error={log.error}
            onRetry={() => void log.refetch()}
            emptyTitleKey="msg:log.empty"
          />
          {log.hasNextPage ? (
            <Button
              onClick={() => void log.fetchNextPage()}
              disabled={log.isFetchingNextPage}
              sx={{ mt: 1 }}
              data-testid="log-more"
            >
              {t('msg:log.more')}
            </Button>
          ) : null}
        </>
      ) : null}
      {open ? <MessageDetailDialog messageId={open.id} onClose={() => setOpen(null)} /> : null}
    </ListPage>
  );
}
